import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useMyContext } from "../Provider/CommonProvider";
import AdminCustomButton from "./AdminCustomButton";
import AdminCustomInput from "./AdminCustomInput";
import {
  IoMdCloudUpload,
  IoMdSearch,
  IoMdClose,
  IoMdCheckmarkCircleOutline,
  IoMdRefresh,
} from "react-icons/io";
import {
  FaFolder,
  FaFolderOpen,
  FaFileVideo,
  FaFileImage,
  FaFileAudio,
  FaFileAlt,
  FaFilePdf,
  FaFileArchive,
  FaChevronRight,
  FaPlayCircle,
  FaServer,
  FaStopCircle,
} from "react-icons/fa";

const TARGETS = [
  {
    value: "storage",
    label: "Storage Zone",
    icon: <FaServer className="me-2" />,
    description:
      "Bulk file storage. Original folder hierarchy is preserved 1:1 on Bunny.",
  },
  {
    value: "stream",
    label: "Stream Library",
    icon: <FaPlayCircle className="me-2" />,
    description:
      "Video transcoding & playback. Each video is uploaded as a Stream object — folder paths aren't preserved here.",
  },
];

// Max selected paths sent to /cdn-sync/plan in a single call.
// Keeping this small prevents the server from recursively enumerating
// thousands of files in one request (the root cause of API crashes).
const PLAN_BATCH_SIZE = 5;

// Files sent to /cdn-sync/upload per request.
const UPLOAD_CHUNK_SIZE = 5;

const FILE_ICONS = {
  video: ["mp4", "mkv", "webm", "mov", "avi", "m3u8", "ts"],
  image: ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"],
  audio: ["mp3", "wav", "aac", "flac", "ogg", "m4a"],
  pdf: ["pdf"],
  archive: ["zip", "rar", "7z", "tar", "gz"],
};

const iconForFile = (name) => {
  const ext = (name?.split(".").pop() || "").toLowerCase();
  if (FILE_ICONS.video.includes(ext)) return <FaFileVideo className="text-primary fs-4" />;
  if (FILE_ICONS.image.includes(ext)) return <FaFileImage className="text-success fs-4" />;
  if (FILE_ICONS.audio.includes(ext)) return <FaFileAudio className="text-info fs-4" />;
  if (FILE_ICONS.pdf.includes(ext)) return <FaFilePdf className="text-danger fs-4" />;
  if (FILE_ICONS.archive.includes(ext)) return <FaFileArchive className="text-warning fs-4" />;
  return <FaFileAlt className="text-secondary fs-4" />;
};

export default function AdminBunnyCdnSyncModal({ open, onClose }) {
  const { fetchRequest } = useMyContext();
  const baseURL = process.env.REACT_APP_Baseurl;
  const treeApi = `${baseURL}/admin/cdn-sync/tree`;
  const planApi = `${baseURL}/admin/cdn-sync/plan`;
  const syncApi = `${baseURL}/admin/cdn-sync/upload`;

  const [currentPath, setCurrentPath] = useState("");
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: "uploads", path: "uploads" }]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Selection is keyed by absolute relative path. Folders are tagged with a
  // trailing '/' so we can tell them apart from files of the same name.
  const [selected, setSelected] = useState(new Map());
  const [searchTerm, setSearchTerm] = useState("");

  // Target destination on Bunny: storage zone OR stream library
  const [target, setTarget] = useState("storage");

  // Sync phase
  const [syncing, setSyncing] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0); // 0–100
  const [syncStage, setSyncStage] = useState("");
  const [results, setResults] = useState(null); // { successCount, errorCount, results: [...] }

  // Stop control — set when the user clicks Stop. The chunk loop checks
  // this between batches; the in-flight axios call is aborted via the
  // controller ref.
  const stopRequestedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const fetchTree = useCallback(
    async (targetPath = "") => {
      setLoading(true);
      try {
        const response = await fetchRequest({
          method: "GET",
          url: treeApi,
          params: { path: targetPath },
        });
        if (response?.status) {
          setCurrentPath(response.currentPath || "");
          setBreadcrumbs(response.breadcrumbs || [{ name: "uploads", path: "" }]);
          setFolders(response.folders || []);
          setFiles(response.files || []);
        } else {
          toast.error(response?.message || "Failed to load directory.");
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load directory.");
      } finally {
        setLoading(false);
      }
    },
    [fetchRequest, treeApi]
  );

  useEffect(() => {
    if (open) {
      setSelected(new Map());
      setSearchTerm("");
      setResults(null);
      setSyncProgress(0);
      setSyncStage("");
      setTarget("storage");
      setStopping(false);
      stopRequestedRef.current = false;
      abortControllerRef.current = null;
      fetchTree("");
    }
  }, [open, fetchTree]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return { folders, files };
    return {
      folders: folders.filter((f) => f.name.toLowerCase().includes(term)),
      files: files.filter((f) => f.name.toLowerCase().includes(term)),
    };
  }, [folders, files, searchTerm]);

  const folderKey = (folderPath) => `folder::${folderPath}`;
  const fileKey = (filePath) => `file::${filePath}`;

  const toggleFolder = (folder) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const key = folderKey(folder.path);
      if (next.has(key)) next.delete(key);
      else next.set(key, { path: folder.path, type: "folder", name: folder.name });
      return next;
    });
  };

  const toggleFile = (file) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const key = fileKey(file.path);
      if (next.has(key)) next.delete(key);
      else next.set(key, { path: file.path, type: "file", name: file.name, size: file.size });
      return next;
    });
  };

  const visibleFiles = filtered.files;
  const visibleFolders = filtered.folders;

  const totalVisibleItems = visibleFiles.length + visibleFolders.length;
  const selectedVisibleCount =
    visibleFiles.filter((f) => selected.has(fileKey(f.path))).length +
    visibleFolders.filter((f) => selected.has(folderKey(f.path))).length;

  // true  → every visible item is ticked
  // false → nothing / partial
  const allItemsSelected = totalVisibleItems > 0 && selectedVisibleCount === totalVisibleItems;
  // partial tick → drive indeterminate state on the master checkbox
  const someItemsSelected = selectedVisibleCount > 0 && !allItemsSelected;

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allItemsSelected) {
        // Deselect everything visible
        visibleFiles.forEach((f) => next.delete(fileKey(f.path)));
        visibleFolders.forEach((f) => next.delete(folderKey(f.path)));
      } else {
        // Select everything visible (files + folders)
        visibleFiles.forEach((f) =>
          next.set(fileKey(f.path), { path: f.path, type: "file", name: f.name, size: f.size })
        );
        visibleFolders.forEach((f) =>
          next.set(folderKey(f.path), { path: f.path, type: "folder", name: f.name })
        );
      }
      return next;
    });
  };

  // Keep the old name as an alias so nothing else needs changing.
  const allFilesSelected = allItemsSelected;
  const toggleSelectAllFiles = toggleSelectAll;

  const navigateTo = (folderPath) => {
    if (loading) return;
    fetchTree(folderPath);
  };

  const removeFromSelection = (key) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Map());

  const selectedPaths = useMemo(() => Array.from(selected.values()).map((v) => v.path), [selected]);
  const selectedItems = useMemo(() => Array.from(selected.entries()), [selected]);

  const handleStop = () => {
    stopRequestedRef.current = true;
    setStopping(true);
    setSyncStage("Stopping… waiting for current batch to wind down.");
    try {
      abortControllerRef.current?.abort();
    } catch { /* abort always succeeds locally */ }
  };

  const handleSync = async () => {
    if (selectedPaths.length === 0) {
      toast.warning("Select at least one file or folder to sync.");
      return;
    }

    const targetMeta = TARGETS.find((t) => t.value === target);
    const targetLabel = targetMeta?.label || "CDN";

    setSyncing(true);
    setStopping(false);
    setResults(null);
    setSyncProgress(2);
    setSyncStage(`Preparing sync to ${targetLabel}…`);
    stopRequestedRef.current = false;

    // ── Split all selected paths into small plan batches ─────────────────────
    // Root cause fix: sending all paths at once to /cdn-sync/plan forces the
    // server to recursively enumerate every file in one request, which exhausts
    // memory / times out and crashes the API. Batching keeps each call small.
    const planBatches = [];
    for (let i = 0; i < selectedPaths.length; i += PLAN_BATCH_SIZE) {
      planBatches.push(selectedPaths.slice(i, i + PLAN_BATCH_SIZE));
    }
    const totalPlanBatches = planBatches.length;

    const accumulated = [];
    let totalFilesPlanned = 0;
    let stopped = false;

    try {
      for (let b = 0; b < totalPlanBatches; b++) {
        if (stopRequestedRef.current) { stopped = true; break; }

        // Each batch occupies an equal slice of the 0–100% progress bar.
        const batchSlice = 100 / totalPlanBatches;
        const batchBase  = b * batchSlice;
        const batchLabel = totalPlanBatches > 1 ? ` (group ${b + 1} of ${totalPlanBatches})` : "";

        // ── Plan phase for this batch ───────────────────────────────────────
        setSyncStage(`Planning upload to ${targetLabel}${batchLabel}…`);
        setSyncProgress(Math.round(batchBase + 2));

        let planResp;
        try {
          planResp = await fetchRequest({
            method: "POST",
            url: planApi,
            data: { paths: planBatches[b], target },
            timeout: 0,
          });
        } catch (err) {
          toast.error(
            `Planning failed${batchLabel}: ${err?.response?.data?.message || err?.message || "Unknown error."}`
          );
          stopped = true;
          break;
        }

        if (!planResp?.status) {
          toast.error(planResp?.message || `Could not plan${batchLabel}.`);
          stopped = true;
          break;
        }

        // Surface non-fatal expansion errors (e.g. non-video when target = stream)
        const planErrors = Array.isArray(planResp.errors) ? planResp.errors : [];
        accumulated.push(...planErrors);

        const plannedFiles = Array.isArray(planResp.files) ? planResp.files : [];

        if (plannedFiles.length === 0) {
          // Nothing uploadable in this batch — advance progress and continue.
          setSyncProgress(Math.min(99, Math.round(batchBase + batchSlice)));
          continue;
        }

        totalFilesPlanned += plannedFiles.length;

        // Plan done — advance to 20 % of this batch's slice.
        setSyncProgress(Math.round(batchBase + batchSlice * 0.2));

        // ── Upload phase for this batch ─────────────────────────────────────
        const uploadChunks = [];
        for (let i = 0; i < plannedFiles.length; i += UPLOAD_CHUNK_SIZE) {
          uploadChunks.push(plannedFiles.slice(i, i + UPLOAD_CHUNK_SIZE));
        }

        let processed = 0;

        for (let c = 0; c < uploadChunks.length; c++) {
          if (stopRequestedRef.current) { stopped = true; break; }

          const chunk = uploadChunks[c];
          setSyncStage(
            `Uploading ${processed + 1}–${processed + chunk.length} of ${plannedFiles.length} to ${targetLabel}${batchLabel}…`
          );

          // Progress: 20 % plan overhead + 80 % upload, all within this batch's slice.
          const uploadFraction = (c + 1) / uploadChunks.length;
          setSyncProgress(Math.min(99, Math.round(
            batchBase + batchSlice * 0.2 + batchSlice * 0.8 * uploadFraction
          )));

          const controller =
            typeof AbortController !== "undefined" ? new AbortController() : null;
          abortControllerRef.current = controller;

          try {
            const resp = await fetchRequest({
              method: "POST",
              url: syncApi,
              data: { paths: chunk.map((f) => f.path), target },
              signal: controller?.signal,
              timeout: 0,
            });
            if (Array.isArray(resp?.results)) accumulated.push(...resp.results);
          } catch (err) {
            const aborted =
              err?.name === "CanceledError" ||
              err?.code === "ERR_CANCELED" ||
              err?.message === "canceled";
            if (aborted) {
              chunk.forEach((f) =>
                accumulated.push({ path: f.path, target, status: "error", message: "Cancelled by user." })
              );
              stopped = true;
              break;
            }
            chunk.forEach((f) =>
              accumulated.push({
                path: f.path,
                target,
                status: "error",
                message: err?.response?.data?.message || err?.message || "Upload failed.",
              })
            );
          }

          processed += chunk.length;
        }

        if (stopped) break;
      }

      abortControllerRef.current = null;
      setSyncProgress(100);

      // ── Summarise ───────────────────────────────────────────────────────────
      const successCount = accumulated.filter((r) => r.status === "success").length;
      const errorCount   = accumulated.length - successCount;

      if (!stopped && totalFilesPlanned === 0) {
        // Every plan batch came back empty (e.g. non-video files for stream target).
        const skipped = accumulated.length;
        setResults({
          status: false,
          target,
          message: `Nothing to upload. ${skipped} item${skipped === 1 ? "" : "s"} skipped.`,
          successCount: 0,
          errorCount: skipped,
          total: skipped,
          results: accumulated,
          stopped: false,
        });
        toast.warning(`No files matched ${targetLabel}.`);
        return;
      }

      const summary = stopped
        ? `Stopped. ${successCount} uploaded, ${errorCount} skipped/failed.`
        : errorCount === 0
        ? `All ${successCount} file${successCount === 1 ? "" : "s"} synced to ${targetLabel}.`
        : `${successCount} succeeded, ${errorCount} failed.`;

      setResults({
        status: !stopped && errorCount === 0,
        target,
        message: summary,
        successCount,
        errorCount,
        total: accumulated.length,
        results: accumulated,
        stopped,
      });

      if (stopped) toast.info(summary);
      else if (errorCount === 0) toast.success(summary);
      else toast.error(summary);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Sync failed.");
    } finally {
      setSyncing(false);
      setStopping(false);
      setSyncStage("");
      stopRequestedRef.current = false;
      abortControllerRef.current = null;
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal fade show d-block bunny-sync-modal"
      tabIndex="-1"
      style={{
        backgroundColor: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
        <div
          className="modal-content border-0 shadow-lg theme-bg-color theme-text-color overflow-hidden"
          style={{ borderRadius: "1.25rem" }}
        >
          {/* Header */}
          <div className="modal-header border-0 p-4 pb-2 d-flex justify-content-between align-items-start">
            <div>
              <h4 className="modal-title fw-bold mb-1 d-flex align-items-center">
                <IoMdCloudUpload className="me-2 text-primary" />
                Sync to CDN
              </h4>
              <p className="text-muted small mb-0">
                Browse <code>uploads/</code>, select files or folders, then push them to{" "}
                <span className="fw-semibold text-primary">
                  {TARGETS.find((t) => t.value === target)?.label}
                </span>
                .
              </p>
            </div>
            <button
              type="button"
              className="btn-close shadow-none p-3 m-0"
              onClick={onClose}
              disabled={syncing}
            />
          </div>

          {/* Body */}
          <div className="modal-body p-4 pt-2">
            {/* Target selector — choose between Storage Zone and Stream Library */}
            <div className="mb-3">
              <div
                className="d-flex gap-2 p-1 rounded-3 theme-bg-color-secondary border theme-border-color-secondary"
                role="tablist"
                aria-label="Bunny sync target"
              >
                {TARGETS.map((opt) => {
                  const active = target === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      disabled={syncing}
                      className={`btn btn-sm flex-fill d-flex align-items-center justify-content-center px-3 py-2 rounded-3 shadow-none ${active ? "btn-primary" : "btn-link text-decoration-none theme-text-color"}`}
                      onClick={() => setTarget(opt.value)}
                    >
                      {opt.icon}
                      <span className="fw-semibold">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
              <small className="text-muted d-block mt-1">
                {TARGETS.find((t) => t.value === target)?.description}
              </small>
            </div>

            {/* Breadcrumbs */}
            <div className="d-flex flex-wrap align-items-center mb-3 small">
              {breadcrumbs.map((crumb, idx) => {
                const isLast = idx === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={crumb.path || "root"}>
                    <button
                      type="button"
                      className={`btn btn-sm p-1 px-2 rounded-3 d-flex align-items-center ${isLast ? "bg-primary-subtle text-primary fw-semibold" : "btn-link text-decoration-none"}`}
                      onClick={() => !isLast && navigateTo(crumb.path)}
                      disabled={loading || isLast}
                    >
                      {idx === 0 ? <FaFolderOpen className="me-1" /> : null}
                      {crumb.name}
                    </button>
                    {!isLast && <FaChevronRight className="text-muted mx-1 small" size={10} />}
                  </React.Fragment>
                );
              })}
              <button
                type="button"
                className="btn btn-sm btn-link text-decoration-none ms-auto d-flex align-items-center"
                onClick={() => fetchTree(currentPath)}
                disabled={loading}
                title="Refresh"
              >
                <IoMdRefresh className={`me-1 ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>

            {/* Search + Select all */}
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-8">
                <div className="position-relative">
                  <AdminCustomInput
                    placeHolder="Filter by name in this folder…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    noMargin={true}
                    className="ps-5 rounded-3"
                  />
                  <IoMdSearch className="position-absolute top-50 translate-middle-y ms-3 text-muted fs-5" />
                </div>
              </div>
              <div className="col-12 col-md-4">
                <button
                  type="button"
                  className={`w-100 h-100 p-2 border rounded-3 d-flex align-items-center justify-content-center shadow-none ${allItemsSelected ? "bg-primary border-primary text-white" : someItemsSelected ? "border-primary text-primary theme-bg-color-secondary" : "theme-border-color-secondary theme-bg-color-secondary theme-text-color"}`}
                  onClick={toggleSelectAll}
                  disabled={totalVisibleItems === 0}
                >
                  <IoMdCheckmarkCircleOutline className="me-2 fs-5" />
                  <span className="fw-bold">
                    {allItemsSelected
                      ? `Deselect All (${totalVisibleItems})`
                      : someItemsSelected
                      ? `Select All (${totalVisibleItems - selectedVisibleCount} more)`
                      : `Select All (${totalVisibleItems})`}
                  </span>
                </button>
              </div>
            </div>

            <div className="row g-3">
              {/* File browser */}
              <div className="col-12 col-lg-8">
                <div
                  className="bunny-sync-list border rounded-3 theme-border-color-secondary"
                  style={{ height: "55vh", overflowY: "auto" }}
                >
                  {loading ? (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      />
                      <span className="text-muted">Loading directory…</span>
                    </div>
                  ) : visibleFolders.length === 0 && visibleFiles.length === 0 ? (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center p-4">
                      <div
                        className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: "70px", height: "70px" }}
                      >
                        <IoMdClose className="text-muted fs-1" />
                      </div>
                      <h6 className="fw-bold mb-1">No items here</h6>
                      <p className="text-muted small mb-0">
                        {searchTerm
                          ? `Nothing matches "${searchTerm}" in this folder.`
                          : "This folder is empty."}
                      </p>
                    </div>
                  ) : (
                    <div className="p-2">
                      {/* ── Master "Select All" header row ── */}
                      <div
                        className="d-flex align-items-center px-2 py-2 mb-2 rounded-3 border-bottom theme-border-color-secondary"
                        style={{ position: "sticky", top: 0, zIndex: 2, backdropFilter: "blur(6px)" }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-0 shadow-none me-3 flex-shrink-0"
                          style={{ width: "1.1rem", height: "1.1rem", cursor: "pointer" }}
                          checked={allItemsSelected}
                          ref={(el) => { if (el) el.indeterminate = someItemsSelected; }}
                          onChange={toggleSelectAll}
                          title={allItemsSelected ? "Deselect all" : "Select all items in this folder"}
                        />
                        <span
                          className="fw-semibold small flex-grow-1"
                          style={{ cursor: "pointer", userSelect: "none" }}
                          onClick={toggleSelectAll}
                        >
                          {allItemsSelected
                            ? `All ${totalVisibleItems} item${totalVisibleItems === 1 ? "" : "s"} selected`
                            : someItemsSelected
                            ? `${selectedVisibleCount} of ${totalVisibleItems} selected`
                            : `Select all ${totalVisibleItems} item${totalVisibleItems === 1 ? "" : "s"}`}
                        </span>
                        {someItemsSelected && (
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 small">
                            {selectedVisibleCount} / {totalVisibleItems}
                          </span>
                        )}
                      </div>

                      {/* Folders first */}
                      {visibleFolders.map((folder) => {
                        const isSelected = selected.has(folderKey(folder.path));
                        return (
                          <div
                            key={`f-${folder.path}`}
                            className={`d-flex align-items-center p-2 mb-1 rounded-3 bunny-sync-row ${isSelected ? "bunny-sync-row-selected" : ""}`}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input mt-0 shadow-none me-3"
                              style={{ width: "1.1rem", height: "1.1rem" }}
                              checked={isSelected}
                              onChange={() => toggleFolder(folder)}
                              title="Select entire folder (recursive)"
                            />
                            <button
                              type="button"
                              className="btn btn-link text-decoration-none p-0 flex-grow-1 d-flex align-items-center text-start theme-text-color"
                              onClick={() => navigateTo(folder.path)}
                            >
                              <FaFolder className="text-warning me-2 fs-5" />
                              <span className="fw-semibold text-truncate">{folder.name}</span>
                            </button>
                            <FaChevronRight className="text-muted ms-2" size={12} />
                          </div>
                        );
                      })}

                      {/* Files */}
                      {visibleFiles.map((file) => {
                        const isSelected = selected.has(fileKey(file.path));
                        return (
                          <div
                            key={`file-${file.path}`}
                            className={`d-flex align-items-center p-2 mb-1 rounded-3 bunny-sync-row ${isSelected ? "bunny-sync-row-selected" : ""}`}
                            onClick={() => toggleFile(file)}
                            role="button"
                          >
                            <input
                              type="checkbox"
                              className="form-check-input mt-0 shadow-none me-3"
                              style={{ width: "1.1rem", height: "1.1rem" }}
                              checked={isSelected}
                              onChange={() => toggleFile(file)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="me-2">{iconForFile(file.name)}</span>
                            <div className="flex-grow-1 min-width-0">
                              <div className="fw-semibold text-truncate" title={file.path}>
                                {file.name}
                              </div>
                              <div className="text-muted small">{file.sizeLabel}</div>
                            </div>
                            {isSelected && (
                              <IoMdCheckmarkCircleOutline className="text-primary fs-4 ms-2" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Selection panel */}
              <div className="col-12 col-lg-4">
                <div
                  className="border rounded-3 theme-border-color-secondary h-100 d-flex flex-column"
                  style={{ height: "55vh", overflow: "hidden" }}
                >
                  <div className="d-flex align-items-center justify-content-between p-3 border-bottom theme-border-color-secondary">
                    <div className="fw-bold d-flex align-items-center flex-wrap gap-1">
                      Selected
                      <span className="badge bg-primary ms-1">{selected.size}</span>
                      {selected.size > 10 && (
                        <span
                          className="badge bg-warning-subtle text-warning border border-warning-subtle px-2"
                          style={{ fontSize: "0.65rem" }}
                          title={`Large selection — syncing in groups of ${PLAN_BATCH_SIZE} to keep the server stable`}
                        >
                          Auto-batched
                        </span>
                      )}
                    </div>
                    {selected.size > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none text-danger p-0"
                        onClick={clearSelection}
                        disabled={syncing}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="flex-grow-1 overflow-auto p-2">
                    {selected.size === 0 ? (
                      <div className="text-center text-muted p-4">
                        <p className="small mb-0">
                          No items selected. Pick files or whole folders from the
                          browser on the left.
                        </p>
                      </div>
                    ) : (
                      selectedItems.map(([key, item]) => (
                        <div
                          key={key}
                          className="d-flex align-items-center p-2 mb-1 rounded-3 theme-bg-color-secondary"
                        >
                          {item.type === "folder" ? (
                            <FaFolder className="text-warning me-2" />
                          ) : (
                            <span className="me-2">{iconForFile(item.name)}</span>
                          )}
                          <div className="flex-grow-1 min-width-0">
                            <div className="text-truncate small fw-semibold" title={item.path}>
                              {item.name}
                            </div>
                            <div className="text-muted text-truncate" style={{ fontSize: "0.7rem" }}>
                              {item.path}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm text-muted p-1 ms-1 shadow-none"
                            onClick={() => removeFromSelection(key)}
                            disabled={syncing}
                            title="Remove from selection"
                          >
                            <IoMdClose />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Sync progress / results */}
            {(syncing || results) && (
              <div className="mt-4 p-3 border rounded-3 theme-border-color-secondary">
                {syncing && (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
                      <span className="fw-semibold flex-grow-1">{syncStage}</span>
                      <span className="text-muted small">{syncProgress}%</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger d-flex align-items-center shadow-none"
                        onClick={handleStop}
                        disabled={stopping}
                      >
                        <FaStopCircle className="me-2" />
                        {stopping ? "Stopping…" : "Stop"}
                      </button>
                    </div>
                    <div className="progress" style={{ height: "10px" }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                        role="progressbar"
                        style={{ width: `${syncProgress}%` }}
                      />
                    </div>
                  </>
                )}

                {results && !syncing && (
                  <>
                    <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                      <span className="fw-bold">
                        Sync Result {results.stopped ? "(stopped)" : ""}:
                      </span>
                      <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2">
                        {results.successCount} succeeded
                      </span>
                      {results.errorCount > 0 && (
                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2">
                          {results.errorCount} failed
                        </span>
                      )}
                      <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2">
                        Target: {TARGETS.find((t) => t.value === results.target)?.label || results.target}
                      </span>
                      <span className="text-muted small ms-auto">
                        {results.total} total file{results.total === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div
                      className="border rounded-3 theme-border-color-secondary"
                      style={{ maxHeight: "30vh", overflowY: "auto" }}
                    >
                      <table className="table table-sm mb-0 small">
                        <thead className="theme-bg-color-secondary">
                          <tr>
                            <th style={{ width: "70px" }}>Status</th>
                            <th>Path</th>
                            <th style={{ width: "100px" }}>Size</th>
                            <th>Detail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.results.map((r, idx) => (
                            <tr key={`${r.path}-${idx}`}>
                              <td>
                                {r.status === "success" ? (
                                  <span className="badge bg-success">OK</span>
                                ) : (
                                  <span className="badge bg-danger">Fail</span>
                                )}
                              </td>
                              <td className="text-break" style={{ maxWidth: "320px" }}>
                                <code className="small">{r.path}</code>
                              </td>
                              <td>{r.sizeLabel || "—"}</td>
                              <td className="text-muted small">{r.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-0 p-3 px-4 d-flex flex-column flex-sm-row gap-2">
            <div className="flex-grow-1 small text-muted">
              {target === "storage" ? (
                <>
                  Files keep the same path on CDN — e.g.{" "}
                  <code>uploads/images/foo.jpg</code> uploads to{" "}
                  <code>{`<storage-zone>/uploads/images/foo.jpg`}</code>.
                </>
              ) : (
                <>
                  Each video becomes a new entry in the configured Stream Library
                  and is transcoded by Bunny. Folder paths are not preserved on
                  Stream.
                </>
              )}
            </div>
            <div className="d-flex gap-2 ms-sm-auto">
              <button
                type="button"
                className="btn btn-link text-muted text-decoration-none fw-semibold px-3 shadow-none border-0"
                onClick={onClose}
                disabled={syncing}
              >
                Close
              </button>
              {syncing ? (
                <button
                  type="button"
                  className="btn btn-danger d-flex align-items-center px-4 rounded-3 shadow-none"
                  onClick={handleStop}
                  disabled={stopping}
                >
                  <FaStopCircle className="me-2" />
                  {stopping ? "Stopping…" : "Stop Sync"}
                </button>
              ) : (
                <AdminCustomButton
                  saveMethod={handleSync}
                  saveText={
                    `Sync ${selected.size || ""} to ${TARGETS.find((t) => t.value === target)?.label || "CDN"}`.trim()
                  }
                  loading={false}
                  disabled={selected.size === 0 || loading}
                  className="px-4 rounded-3"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bunny-sync-row {
          transition: background-color 0.15s ease-in-out, transform 0.15s ease-in-out;
          cursor: pointer;
        }
        .bunny-sync-row:hover {
          background-color: rgba(13, 110, 253, 0.07);
        }
        .bunny-sync-row-selected {
          background-color: rgba(13, 110, 253, 0.12);
        }
        .bunny-sync-list::-webkit-scrollbar { width: 6px; }
        .bunny-sync-list::-webkit-scrollbar-track { background: transparent; }
        .bunny-sync-list::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 10px; }
        .bunny-sync-modal code { font-size: 0.78rem; }
      `}</style>
    </div>
  );
}
